import { styled } from '@mui/material/styles';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ArchiveIcon from '@mui/icons-material/Archive';
import UnarchiveIcon from '@mui/icons-material/Unarchive';
import DeleteIcon from '@mui/icons-material/Delete';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import StatusChip, { STATUS_PALETTE } from '@components/StatusChip';
import MoreMenu from '@components/MoreMenu';
import Tooltip from '@components/Tooltip';
import Button from '@components/Button';
import { Typography } from '@components/Typography';
import { useTranslation } from '@hooks';
import BubblePreview from '@features/WhatsAppTemplateBuilder/BubblePreview';
import { TEMPLATE_CHANNEL_FILTER, WA_TEMPLATE_STATUS } from '@constants/campaignTemplates';
import TemplateThumbnail from './TemplateThumbnail';

const Card = styled('div')({
    border: '1px solid var(--divider)',
    borderRadius: 8,
    background: '#fff',
    cursor: 'pointer',
    overflow: 'hidden',
    transition: 'box-shadow 0.15s',
    '&:hover': {
        boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
    },
    '&:hover .pl-card-preview-overlay': {
        opacity: 1,
    },
});

const PreviewWrap = styled('div')({
    position: 'relative',
});

const PreviewOverlay = styled('div')({
    position: 'absolute',
    inset: 0,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    background: 'rgba(0, 0, 0, 0.45)',
    color: '#fff',
    opacity: 0,
    transition: 'opacity 0.15s',
    '& svg': {
        width: 28,
        height: 28,
    },
});

const CardBody = styled('div')({
    padding: '12px 16px',
});

const CardMeta = styled('div')({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
    marginTop: 8,
});

const BubbleWrap = styled('div')({
    position: 'relative',
    background: '#E5DDD5',
    padding: '16px 12px',
    height: 180,
    overflow: 'hidden',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'flex-start',
    '&::after': {
        content: '""',
        position: 'absolute',
        left: 0,
        right: 0,
        bottom: 0,
        height: 32,
        background: 'linear-gradient(transparent, #E5DDD5)',
        pointerEvents: 'none',
    },
});

const WA_STATUS_TONE = {
    [WA_TEMPLATE_STATUS.APPROVED]: 'success',
    [WA_TEMPLATE_STATUS.PENDING]: 'info',
    [WA_TEMPLATE_STATUS.REJECTED]: 'danger',
    [WA_TEMPLATE_STATUS.PAUSED]: 'warning',
    [WA_TEMPLATE_STATUS.DISABLED]: 'warning',
    [WA_TEMPLATE_STATUS.DRAFT]: 'neutral',
};

const TemplateCard = ({
    template,
    canManage,
    onOpen,
    onDuplicate,
    onArchive,
    onDelete,
    menuAnchor,
    onOpenMenu,
    onCloseMenu,
}) => {
    const { t } = useTranslation();

    const isWhatsApp = template.channel === TEMPLATE_CHANNEL_FILTER.WHATSAPP;

    const isReferenced = template.usedByActiveCampaignsCount > 0;

    const menuIconSx = { height: '20px', width: '20px', fill: 'var(--text-secondary)' };

    const menu = [
        {
            id: 'duplicate',
            title: t('TemplateLibrary.duplicate'),
            icon: <ContentCopyIcon sx={menuIconSx} />,
            onClick: () => onDuplicate(template),
        },
        {
            id: 'archive',
            title: template.isArchived ? t('TemplateLibrary.unarchive') : t('TemplateLibrary.archive'),
            icon: template.isArchived ? <UnarchiveIcon sx={menuIconSx} /> : <ArchiveIcon sx={menuIconSx} />,
            onClick: () => onArchive(template),
        },
        {
            id: 'delete',
            title: t('buttons.delete'),
            icon: <DeleteIcon sx={menuIconSx} />,
            disabled: isReferenced,
            tooltip: isReferenced
                ? t('TemplateLibrary.usedByCampaigns', { count: template.usedByActiveCampaignsCount })
                : null,
            onClick: () => onDelete(template),
        },
    ];

    const waStatusChip = isWhatsApp ? (
        <StatusChip
            bg={STATUS_PALETTE[WA_STATUS_TONE[template.status] || 'neutral'].bg}
            fg={STATUS_PALETTE[WA_STATUS_TONE[template.status] || 'neutral'].fg}
            label={t(`TemplateLibrary.waStatus_${template.status}`)}
        />
    ) : null;

    return (
        <Card onClick={() => onOpen(template)}>
            <PreviewWrap>
                {isWhatsApp ? (
                    <BubbleWrap>
                        <BubblePreview
                            components={template.components}
                            headerMediaUrl={template.headerMediaUrl}
                            mappings={template.variableMappings}
                        />
                    </BubbleWrap>
                ) : (
                    <TemplateThumbnail html={template.compiledHtml} title={template.name} />
                )}
                <PreviewOverlay className="pl-card-preview-overlay">
                    {canManage ? <EditOutlinedIcon /> : <VisibilityOutlinedIcon />}
                    <Typography variant="subtitle2" sx={{ color: '#fff' }}>
                        {canManage ? t('buttons.edit') : t('common.view')}
                    </Typography>
                </PreviewOverlay>
            </PreviewWrap>
            <CardBody onClick={e => e.stopPropagation()}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography
                        variant="subtitle2"
                        sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: 'pointer' }}
                        onClick={() => onOpen(template)}
                    >
                        {template.name}
                    </Typography>
                    {canManage && (
                        <MoreMenu
                            id={template.id}
                            menu={menu}
                            anchor={menuAnchor}
                            handleOpenEl={onOpenMenu}
                            handleCloseEl={onCloseMenu}
                        />
                    )}
                </div>
                <CardMeta>
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                        {isWhatsApp ? (
                            <>
                                <StatusChip
                                    bg={STATUS_PALETTE.info.bg}
                                    fg={STATUS_PALETTE.info.fg}
                                    label={t('TemplateLibrary.channelWhatsApp')}
                                />
                                {template.status === WA_TEMPLATE_STATUS.REJECTED && template.rejectionReason ? (
                                    <Tooltip title={template.rejectionReason} placement="top">
                                        <span>{waStatusChip}</span>
                                    </Tooltip>
                                ) : (
                                    waStatusChip
                                )}
                                {!template.isFullyMapped && (
                                    <StatusChip
                                        bg={STATUS_PALETTE.warning.bg}
                                        fg={STATUS_PALETTE.warning.fg}
                                        label={t('TemplateLibrary.waNeedsSetup')}
                                    />
                                )}
                                {template.isDisconnected && (
                                    <StatusChip
                                        bg={STATUS_PALETTE.danger.bg}
                                        fg={STATUS_PALETTE.danger.fg}
                                        label={t('TemplateLibrary.waDisconnected')}
                                    />
                                )}
                            </>
                        ) : (
                            <StatusChip
                                bg={STATUS_PALETTE.info.bg}
                                fg={STATUS_PALETTE.info.fg}
                                label={t('TemplateLibrary.channelEmail')}
                            />
                        )}
                        {template.isSystem && (
                            <StatusChip
                                bg={STATUS_PALETTE.neutral.bg}
                                fg={STATUS_PALETTE.neutral.fg}
                                label={t('TemplateLibrary.systemTemplate')}
                            />
                        )}
                        {template.isArchived && (
                            <StatusChip
                                bg={STATUS_PALETTE.warning.bg}
                                fg={STATUS_PALETTE.warning.fg}
                                label={t('TemplateLibrary.archived')}
                            />
                        )}
                    </div>
                    <Typography variant="caption" sx={{ color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>
                        {template.isSystem
                            ? null
                            : t('TemplateLibrary.usedByCampaigns', { count: template.usedByCampaignsCount })}
                    </Typography>
                </CardMeta>
                {isWhatsApp && template.status === WA_TEMPLATE_STATUS.REJECTED && canManage && (
                    <Button variant="text" size="small" onClick={() => onOpen(template)}>
                        {t('TemplateLibrary.waEditResubmit')}
                    </Button>
                )}
            </CardBody>
        </Card>
    );
};

export default TemplateCard;
